import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, InboxIcon, CrownIcon, BriefcaseIcon, ArrowRightIcon, LayoutGridIcon, UsersIcon, Share2Icon } from 'lucide-react';
import { useBusiness, getHubTabs, BUSINESS_TYPE_LABEL } from '../components/BusinessContext';
import { BusinessDashboard } from '../components/BusinessDashboard';
import { BusinessCatalog } from '../components/BusinessCatalog';
import { BusinessLeads } from '../components/BusinessLeads';
import { BusinessCustomers } from '../components/BusinessCustomers';
import { BusinessOrders } from '../components/BusinessOrders';
import { BusinessSettings } from '../components/BusinessSettings';
import { MyShop } from '../components/MyShop';
import { UpgradePlanModal } from '../components/UpgradePlanModal';
import { OrdersUnlockedModal } from '../components/orders/OrdersUnlockedModal';
import { ShopStudioProvider } from '../contexts/ShopStudioContext';
import { BusinessHubWelcomeModal } from '../components/BusinessHubWelcomeModal';
import { ModuleIntro } from '../components/hub/ModuleIntro';
import { SetupProgressPill } from '../components/hub/SetupProgressPill';
import { SetupStepId } from '../data/hubSetup';
import { ConsentCheckbox } from '../components/legal/ConsentCheckbox';
export function BusinessHub() {
  const {
    hub,
    activateHub,
    completeSetupStep,
    trialActive,
    trialDaysRemaining,
    trialPlan,
    subscribed,
    orderingEnabled,
    ordersAnnounced,
    dismissOrdersAnnouncement,
    ordersTabNew,
    markOrdersSeen
  } = useBusiness();
  const tabs = getHubTabs(hub?.type ?? 'generic', orderingEnabled);
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // Opening a step's module counts as having worked through it — the guide
  // then moves on. Publishing is the exception: it only counts when the shop
  // is actually live, which the guide reads from the shop itself.
  useEffect(() => {
    const visited: Record<string, SetupStepId> = {
      Settings: 'details',
      [tabs[1]]: 'catalogue',
      'My Shop': 'shop',
      Leads: 'leads'
    };
    const step = visited[activeTab];
    if (step) completeSetupStep(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  // If ordering is switched off while Orders is open, fall back to Dashboard.
  useEffect(() => {
    if (!orderingEnabled && activeTab === 'Orders') setActiveTab(tabs[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderingEnabled]);
  if (!hub?.active) {
    return <ActivateHubScreen onActivate={() => activateHub('rental')} data-id="element-5240" data-mp="1fh11956olm3" />;
  }
  const businessName = hub.name || 'Your Business';
  return <ShopStudioProvider data-id="element-5241" data-mp="6d8qlwicvvn4">
    <div className="flex-1 flex flex-col h-[calc(100vh-57px)] lg:h-[calc(100vh-80px)] bg-[#F8FAFC]" data-id="element-5242" data-mp="1cvfctkrqxlr0">
      {/* Hub Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-100 px-4 lg:px-8 pt-6" data-id="element-5243" data-mp="1htpb448rp2gu">
        <div className="max-w-6xl mx-auto w-full" data-id="element-5244" data-mp="1ryyrhgum4cuc">
          <div className="flex items-center justify-between gap-3 mb-5 lg:pr-52" data-id="element-5245" data-mp="1s0jiwxlmrfnz">
            <div className="flex items-center gap-3" data-id="element-5246" data-mp="1mzvtuhgyp8we">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-md" data-id="element-5247" data-mp="1i840y0ac2r33">
                <SparklesIcon className="w-6 h-6 text-white" data-id="element-5248" data-mp="1e6s9dp7wfvs9" />
              </div>
              <div data-id="element-5249" data-mp="198qp50yqlqf7">
                <div className="flex items-center gap-2" data-id="element-5250" data-mp="1998wotqunr2f">
                  <h1 className="font-display font-bold text-xl text-dark-navy leading-tight" data-id="element-5251" data-mp="148l3q2j2905k">
                    {businessName}
                  </h1>
                  {subscribed && <button onClick={() => setUpgradeOpen(true)} title="Manage plan" className="group inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400/15 to-amber-500/10 border border-amber-400/40 pl-1.5 pr-2 py-0.5 transition-colors hover:from-amber-400/25 hover:to-amber-500/20" data-id="element-5252" data-mp="v8of20wo81ai">
                      <CrownIcon className="w-3 h-3 text-amber-500 fill-amber-400/70" data-id="element-5253" data-mp="31p0c4k0t80i9" />
                      <span className="text-[11px] font-bold tracking-wide text-amber-700 leading-none" data-id="element-5254" data-mp="2nozah2cq0p1q">
                        {trialPlan || 'Premium'}
                      </span>
                    </button>}
                </div>
                <p className="text-sm text-slate-500" data-id="element-5255" data-mp="2dxbhgx0imzy5">
                  {BUSINESS_TYPE_LABEL[hub.type]} · Business Hub
                </p>
              </div>
            </div>
            <SetupProgressPill onOpen={() => setActiveTab('Dashboard')} data-id="element-5256" data-mp="241rj83yhbp68" />
          </div>

          {/* Top Tab Navigation */}
          <div className="flex gap-1 overflow-x-auto no-scrollbar -mb-px" data-id="element-5257" data-mp="2902velc8ybrm">
            {tabs.map(tab => {
              const isActive = tab === activeTab;
              const isNew = tab === 'Orders' && ordersTabNew;
              return <button key={tab} onClick={() => {
                setActiveTab(tab);
                if (tab === 'Orders') markOrdersSeen();
              }} className={`relative px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${isActive ? 'text-primary-indigo' : isNew ? 'text-primary-indigo' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-5258" data-mp="1gd38jqfw924t">
                  <span className="inline-flex items-center gap-2" data-id="element-5259" data-mp="1zaw60d7r0jut">
                    {tab}
                    {isNew && <span className="relative inline-flex items-center" data-id="element-5260" data-mp="1ub018br88oe7">
                        {/* One soft halo, three times — enough to catch the
                            eye without becoming a flashing banner. */}
                        <motion.span initial={{
                      opacity: 0.55,
                      scale: 1
                    }} animate={{
                      opacity: 0,
                      scale: 2.4
                    }} transition={{
                      duration: 1.4,
                      ease: 'easeOut',
                      repeat: 3,
                      repeatDelay: 0.5
                    }} className="absolute inset-0 rounded-full bg-primary-indigo" data-id="element-5261" data-mp="2i2f9el5opzxd" />
                        <span className="relative px-1.5 py-0.5 rounded-full bg-primary-indigo text-white text-[10px] font-bold tracking-wide leading-none" data-id="element-5262" data-mp="1yfs30qmfd3v3">
                          NEW
                        </span>
                      </span>}
                  </span>
                  {isActive && <motion.div layoutId="hubTabIndicator" className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-indigo rounded-full" data-id="element-5263" data-mp="91c2qf24un77" />}
                </button>;
            })}
          </div>
        </div>
      </div>

      {/* Trial banner — one message, one measure, one action */}
      {hub?.active && trialActive && !subscribed && <div className="bg-white border-b border-slate-200 px-4 lg:px-8 py-3" data-id="element-5264" data-mp="3rz0uvetazplp">
          <div className="mx-auto max-w-6xl flex items-center gap-4" data-id="element-5265" data-mp="3nxxfsuj2xxlj">
            <span className="w-9 h-9 rounded-xl bg-primary-indigo/[0.07] flex items-center justify-center flex-shrink-0" data-id="element-5266" data-mp="oiah2drhc89g">
              <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-5267" data-mp="jjqvwfkpn0i5" />
            </span>
            <div className="min-w-0" data-id="element-5268" data-mp="fgaxpeszss3c">
              <p className="text-sm font-bold text-dark-navy truncate" data-id="element-5269" data-mp="ff9cihi9ip7w">
                {trialDaysRemaining} days left on your{' '}
                {trialPlan ?? 'free'} trial
              </p>
              <p className="text-xs text-slate-500 mt-0.5 truncate" data-id="element-5270" data-mp="308w7r8e5sogs">
                Your shop, orders and AI keep running when you upgrade.
              </p>
            </div>
            <div className="hidden md:block flex-1 max-w-[220px] ml-auto" data-id="element-5271" data-mp="z40ref72yon4">
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden" data-id="element-5272" data-mp="1376x9lqa6dt2">
                <div className="h-full rounded-full bg-primary-indigo" style={{
                width: `${Math.max(6, Math.min(100, trialDaysRemaining / 14 * 100))}%`
              }} data-id="element-5273" data-mp="p6w6sjsvv18f" />
              </div>
            </div>
            <button onClick={() => setUpgradeOpen(true)} className="ml-auto md:ml-0 px-4 py-2 rounded-xl bg-primary-indigo text-white text-xs font-bold hover:bg-indigo-700 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-5274" data-mp="1hxxoark1t8v4">
              See plans
            </button>
          </div>
        </div>}

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 lg:px-8 py-8" data-id="element-5275" data-mp="20suvmc6ar678">
        <div className={`mx-auto w-full ${activeTab === 'Settings' ? 'max-w-[1500px]' : 'max-w-6xl h-full'}`} data-id="element-5276" data-mp="25lflj2m30z5h">
          {activeTab !== 'Dashboard' && <ModuleIntro key={activeTab} tab={activeTab} catalogLabel={tabs[1]} onNavigate={setActiveTab} data-id="element-5277" data-mp="3lchtbtmostrd" />}
          {activeTab === 'Dashboard' ? <BusinessDashboard onNavigate={setActiveTab} data-id="element-5278" data-mp="3v21ghbhfn9s1" /> : activeTab === tabs[1] ? <BusinessCatalog data-id="element-5279" data-mp="37bxd9585pc1k" /> : activeTab === 'Leads' ? <BusinessLeads data-id="element-5280" data-mp="32dvrudn9d0mx" /> : activeTab === 'Customers' ? <BusinessCustomers data-id="element-5281" data-mp="32fp202x2y2zo" /> : activeTab === 'Orders' ? <BusinessOrders onNavigate={setActiveTab} data-id="element-5282" data-mp="3ge6ndtzij3ef" /> : activeTab === 'My Shop' ? <MyShop data-id="element-5283" data-mp="3bdirov0c9ydv" /> : activeTab === 'Settings' ? <BusinessSettings data-id="element-5284" data-mp="vmmummlygpr5" /> : <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex flex-col items-center justify-center text-center py-24" data-id="element-5285" data-mp="vlbhhw6me5nd">
              <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 flex items-center justify-center mb-5 shadow-sm" data-id="element-5286" data-mp="3sskou2efhbq2">
                <InboxIcon className="w-8 h-8 text-slate-300" data-id="element-5287" data-mp="3oqfvl4rw6h3y" />
              </div>
              <h2 className="font-display font-bold text-2xl text-dark-navy mb-2" data-id="element-5288" data-mp="3jsecijuguhsh">
                No {activeTab.toLowerCase()} yet
              </h2>
              <p className="text-slate-500 max-w-md" data-id="element-5289" data-mp="3ev4gumghh9w7">
                This is where your {activeTab.toLowerCase()} will appear once
                you set up your Business Hub. Ask Talab to help you get started.
              </p>
            </motion.div>}
        </div>
      </div>

      {upgradeOpen && <UpgradePlanModal onClose={() => setUpgradeOpen(false)} data-id="element-5290" data-mp="14108okvsncjl" />}
      <BusinessHubWelcomeModal onStart={() => setActiveTab('Dashboard')} data-id="element-5291" data-mp="141rwvan2a7zu" />

      <AnimatePresence data-id="element-5292" data-mp="2lxtwjt30bj2f">
        {ordersAnnounced && <OrdersUnlockedModal onClose={dismissOrdersAnnouncement} onOpenOrders={() => {
          dismissOrdersAnnouncement();
          markOrdersSeen();
          setActiveTab('Orders');
        }} data-id="element-5293" data-mp="2qxgak4qnt3ca" />}
      </AnimatePresence>
    </div>
    </ShopStudioProvider>;
}
const HUB_PERKS = [{
  icon: LayoutGridIcon,
  title: 'Unified catalogue',
  desc: 'Manage every listing, publish to Talab, and stay live on your own widget.'
}, {
  icon: Share2Icon,
  title: 'Your own shop link',
  desc: 'Get a shareable shop link to send customers — your storefront, your brand.'
}, {
  icon: UsersIcon,
  title: 'Leads & team',
  desc: 'Capture every inquiry, track intent, and bring your team into one workspace.'
}];
function ActivateHubScreen({
  onActivate
}: {
  onActivate: () => void;
}) {
  // Business terms must be accepted explicitly before the hub switches on.
  const [agreed, setAgreed] = useState(false);
  const [nudge, setNudge] = useState(false);
  const activate = () => {
    if (!agreed) {
      setNudge(true);
      return;
    }
    onActivate();
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto" data-id="element-5294" data-mp="229u9qw8kuz8q">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="absolute inset-0 bg-dark-navy/55 backdrop-blur-sm" data-id="element-5295" data-mp="1xc2a1g1oy8tz" />
      <div className="relative w-full max-w-2xl my-auto" data-id="element-5296" data-mp="14oohrv8xu1g7">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 280,
        damping: 26
      }} className="relative bg-white rounded-3xl border border-slate-200/70 shadow-2xl overflow-hidden" data-id="element-5297" data-mp="1jc161rq4qlaa">
          {/* Soft brand glow */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary-indigo/10 blur-3xl" data-id="element-5298" data-mp="32az36ejprn17" />
          <div className="pointer-events-none absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-ai-purple/10 blur-3xl" data-id="element-5299" data-mp="2okc42la1jv4l" />

          <div className="relative px-7 lg:px-12 py-12 text-center" data-id="element-5300" data-mp="2skw3jvi37atw">
            <motion.div initial={{
            scale: 0.8,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.1,
            type: 'spring',
            stiffness: 200,
            damping: 16
          }} className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-indigo to-ai-purple shadow-lg shadow-primary-indigo/30 mb-6" data-id="element-5301" data-mp="3gb9shd5dktk9">
              <BriefcaseIcon className="w-8 h-8 text-white" data-id="element-5302" data-mp="2g3mssy8qkj0j" />
            </motion.div>

            <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 rounded-full bg-primary-indigo/5 border border-primary-indigo/10 text-[11px] font-bold uppercase tracking-wider text-primary-indigo" data-id="element-5303" data-mp="2bcmz01uwyh29">
              <SparklesIcon className="w-3.5 h-3.5" data-id="element-5304" data-mp="22c89sretwhth" /> Business Hub
            </span>

            <h1 className="font-display font-bold text-2xl lg:text-3xl text-dark-navy mb-3 leading-tight" data-id="element-5305" data-mp="2z1pdx1y5xvt2">
              Turn Talab into your selling workspace
            </h1>
            <p className="text-slate-500 max-w-md mx-auto leading-relaxed mb-9" data-id="element-5306" data-mp="2u11r6sgyw14n">
              Activate your Business Hub to publish listings, capture leads, and
              get your own shareable shop link to send to customers — all in one
              place.
            </p>

            {/* Standout CTA */}
            <div className="flex flex-col items-center gap-3 mb-10" data-id="element-5307" data-mp="19n167e0ip21d">
              <div className="w-full max-w-sm mb-2" data-id="element-5308" data-mp="19p42ugw6dtf2">
                <ConsentCheckbox checked={agreed} onChange={v => {
                setAgreed(v);
                if (v) setNudge(false);
              }} docs={['businessTerms', 'privacy']} lead="I agree to Talab’s" attention={nudge} data-id="element-5309" data-mp="3lb5ycpuckb8s" />
                {nudge && !agreed && <p role="alert" className="text-[12px] text-error mt-2 text-left ml-[30px]" data-id="element-5310" data-mp="7w9lrg20k3vw">
                    Please agree to the terms to activate.
                  </p>}
              </div>
              <motion.button onClick={activate} aria-disabled={!agreed} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className={`group relative inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl bg-gradient-to-r from-primary-indigo to-ai-purple text-white font-semibold text-base shadow-xl shadow-primary-indigo/30 overflow-hidden transition-opacity duration-150 ease-out ${agreed ? '' : 'opacity-60'}`} data-id="element-5311" data-mp="hupfzrwc7t9m">
                {/* animated shine */}
                <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/25 to-transparent" data-id="element-5312" data-mp="3u57r5dxdlvy" />
                <SparklesIcon className="w-5 h-5 relative" data-id="element-5313" data-mp="dox3s4rv00om" />
                <span className="relative" data-id="element-5314" data-mp="8qvjxhmlzxc3">Activate Business Hub</span>
                <ArrowRightIcon className="w-5 h-5 relative transition-transform group-hover:translate-x-0.5" data-id="element-5315" data-mp="nhb8n6e8hnf0" />
              </motion.button>
              <p className="text-xs text-slate-400" data-id="element-5316" data-mp="ii8g2lffx9nx">
                Free to start · No credit card required
              </p>
            </div>

            {/* Perks */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left" data-id="element-5317" data-mp="rj69yaw600ss">
              {HUB_PERKS.map((perk, i) => {
              const Icon = perk.icon;
              return <motion.div key={perk.title} initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2 + i * 0.08
              }} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4" data-id="element-5318" data-mp="1g3t4pk6p1b1a" data-mpi={i}>
                    <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center mb-3 shadow-sm" data-id="element-5319" data-mp="igapbqij1aob">
                      <Icon className="w-4.5 h-4.5 text-primary-indigo" data-id="element-5320" data-mp="nevqpt9im0ec" />
                    </div>
                    <h3 className="font-bold text-sm text-dark-navy mb-1" data-id="element-5321" data-mp="9fmgrbbwk2it">
                      {perk.title}
                    </h3>
                    <p className="text-xs text-slate-500 leading-relaxed" data-id="element-5322" data-mp="tvp0axhrohcg">
                      {perk.desc}
                    </p>
                  </motion.div>;
            })}
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}